import { X } from 'lucide-react';
import { Dialog as RadixDialog } from 'radix-ui';
import { type ReactNode, type RefObject, useEffect, useRef, useState } from 'react';
import { cn } from '../../../lib/cn';
import { useReturnFocus } from '../../../ui/useReturnFocus';

/** Szerokość panelu na desktopie (AppShell zwęża treść o tyle na szerokich ekranach). */
export const COPILOT_DOCK_WIDTH = 420;

const DESKTOP_QUERY = '(min-width: 768px)';

function canQuery(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function';
}

/** Czy ekran jest desktopowy (≥ 768 px, jak `md:` w Tailwind); reaguje na zmianę szerokości okna. */
export function useIsDesktop(): boolean {
  const [desktop, setDesktop] = useState(() => (canQuery() ? window.matchMedia(DESKTOP_QUERY).matches : false));
  useEffect(() => {
    if (!canQuery()) return;
    const mql = window.matchMedia(DESKTOP_QUERY);
    const onChange = () => setDesktop(mql.matches);
    onChange();
    mql.addEventListener?.('change', onChange);
    return () => mql.removeEventListener?.('change', onChange);
  }, []);
  return desktop;
}

/**
 * Panel czatu Copilota (Fala 10, bąbel AI): na desktopie DOKOWANY z prawej (420 px, bez przyciemnienia i bez pułapki
 * fokusu — treść obok zostaje dostępna: można klikać, przewijać i przechodzić między ekranami z otwartym czatem;
 * od 1600 px treść zwęża się o szerokość panelu, wężej panel leży nad prawą częścią treści), na telefonie pełny ekran
 * (modalny, fokus w panelu).
 * Esc zamyka, gdy fokus jest w panelu (albo nigdzie) — Esc w polu na stronie (np. edycja tytułu) panelu nie zamyka.
 * Po zamknięciu fokus wraca na element, który panel otworzył (bąbel, przycisk, ⌘K → treść strony).
 */
export function CopilotDock({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  initialFocusRef,
  id,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  initialFocusRef?: RefObject<HTMLTextAreaElement | HTMLInputElement | HTMLElement | null>;
  id?: string;
}) {
  const desktop = useIsDesktop();
  const contentRef = useRef<HTMLDivElement>(null);
  const onCloseAutoFocus = useReturnFocus(open);
  const onOpenAutoFocus = initialFocusRef
    ? (event: Event) => {
        const el = initialFocusRef.current;
        if (!el) return;
        event.preventDefault();
        el.focus();
        if (el instanceof HTMLTextAreaElement || el instanceof HTMLInputElement) {
          const end = el.value.length;
          el.setSelectionRange(end, end);
        }
      }
    : undefined;

  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange} modal={!desktop}>
      <RadixDialog.Portal>
        <RadixDialog.Content
          ref={contentRef}
          id={id}
          onCloseAutoFocus={onCloseAutoFocus}
          {...(onOpenAutoFocus ? { onOpenAutoFocus } : {})}
          {...(description ? {} : { 'aria-describedby': undefined })}
          {...(desktop
            ? {
                // dokowany: klik i fokus w treści strony nie zamykają panelu
                onInteractOutside: (event: Event) => event.preventDefault(),
                onEscapeKeyDown: (event: KeyboardEvent) => {
                  const active = document.activeElement;
                  if (active && active !== document.body && !contentRef.current?.contains(active)) {
                    event.preventDefault();
                  }
                },
              }
            : {})}
          data-dock={desktop ? 'side' : 'full'}
          className={cn(
            'fixed z-50 flex flex-col bg-canvas focus:outline-none',
            // telefon: pełny ekran (nad dolnym menu), desktop: kolumna z prawej
            'inset-0 safe-bottom animate-sheet-up',
            'md:inset-y-0 md:right-0 md:left-auto md:w-[420px] md:animate-sheet-in md:border-l md:border-line md:shadow-modal',
          )}
        >
          <div className="flex items-start gap-4 px-5 pt-[max(20px,env(safe-area-inset-top))] pb-3 md:pt-5">
            <div className="min-w-0 flex-1">
              <RadixDialog.Title className="text-h4 text-fg">{title}</RadixDialog.Title>
              {description && (
                <RadixDialog.Description className="mt-1 text-body-s text-fg-secondary">
                  {description}
                </RadixDialog.Description>
              )}
            </div>
            <RadixDialog.Close
              aria-label="Zamknij"
              className="-mr-2 inline-flex size-8 shrink-0 items-center justify-center rounded-md text-fg-muted hover:bg-elevated hover:text-fg focus-visible:outline-2 focus-visible:outline-accent"
            >
              <X size={18} strokeWidth={1.75} aria-hidden />
            </RadixDialog.Close>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-5">{children}</div>
          {footer && <div className="border-t border-line px-5 py-3">{footer}</div>}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
